import { HttpError } from '@studio/api/errors';
import withOptions from '@studio/api/utils/withOptions';
import identityApi from '@studio/api-identity';
import usersApi from '@studio/api-users';
import type { RequestContext } from '@studio/utils/requestContext';

interface CustomAttributesAPI {
  getCustomAttributes(
    context: RequestContext,
    customAttributesId: string,
  ): Promise<unknown>;

  setCustomAttributes(
    context: RequestContext,
    customAttributesId: string,
    customAttribute: unknown,
  ): Promise<void>;
}

export type LoginMode = 'MODERN' | 'HYBRID' | 'LEGACY';

function getApi(mode: LoginMode): CustomAttributesAPI {
  return mode === 'MODERN' ? identityApi : usersApi;
}

export interface CustomAttributes<
  out ID extends string,
  in out Attributes extends object,
  out DefaultAttributes extends Attributes | null,
> {
  readonly id: ID;
  readonly defaultAttributes: DefaultAttributes;

  isAttributes(attributes: unknown): attributes is Attributes;

  getAttributes(
    context: RequestContext,
  ): Promise<Attributes | DefaultAttributes>;

  setAttributes(
    context: RequestContext,
    attributes: Attributes,
  ): Promise<Attributes>;
}

export function createCustomAttributes<
  const ID extends string,
  const Attributes extends object,
>(
  id: ID,
  isAttributes: (attributes: unknown) => attributes is Attributes,
): CustomAttributes<ID, Attributes, null>;

export function createCustomAttributes<
  const ID extends string,
  const Attributes extends object,
>(
  id: ID,
  isAttributes: (attributes: unknown) => attributes is Attributes,
  defaultAttributes: Attributes,
): CustomAttributes<ID, Attributes, Attributes>;

export function createCustomAttributes<
  const ID extends string,
  const Attributes extends object,
  const DefaultAttributes extends Attributes | null = null,
>(
  id: ID,
  isAttributes: (attributes: unknown) => attributes is Attributes,
  defaultAttributes: DefaultAttributes = null as DefaultAttributes,
): CustomAttributes<ID, Attributes, DefaultAttributes> {
  return {
    id,
    defaultAttributes,

    isAttributes,

    getAttributes: withOptions(
      { experiences: null, name: `getCustomAttributes[${id}]` },
      async function getAttributes(context) {
        if (context.loginMode === null) {
          throw new Error('Missing login mode');
        }

        const api = getApi(context.loginMode);

        try {
          const attributes = await api.getCustomAttributes(context, id);
          if (!isAttributes(attributes)) {
            console.warn(
              `Invalid attributes in ${id}, falling back to defaults!`,
              { before: attributes, after: defaultAttributes },
            );
            return defaultAttributes;
          }

          return attributes;
        } catch (error) {
          if (error instanceof HttpError && error.status === 404) {
            return defaultAttributes;
          }

          throw error;
        }
      },
    ),

    async setAttributes(context, attributes) {
      if (!isAttributes(attributes)) {
        console.warn(`Invalid attributes in ${id}`, attributes);
        throw new TypeError(`Tried to insert invalid attributes in ${id}`);
      }

      if (context.loginMode === null) {
        throw new Error('Missing login mode');
      }

      const api = getApi(context.loginMode);

      await api.setCustomAttributes(context, id, attributes);

      return attributes;
    },
  };
}
